import { useEffect, useState } from "react";
import { ClockDirection, Game } from "../types/models";
import { readClock } from "../utils/gameClock";

/** How often a running clock is re-read. Fine enough that the seconds never
 *  visibly stick, coarse enough not to re-render four times a second for
 *  nothing: React drops the render when the second has not changed. */
const TICK_MS = 250;

/**
 * The seconds a game clock reads right now, ticking while it runs.
 *
 * Elapsed time is measured from the moment the server's reading arrived, using
 * the device's own stopwatch — never by comparing the server's timestamp with
 * the device's clock, which may be minutes out. Each new reading from the API
 * restarts the stopwatch, so a poll or a save re-syncs the display.
 *
 * The reading is computed during render rather than held in state, so a game
 * that has only just loaded — or a poll that lands mid-tick — shows its own
 * time straight away instead of the previous reading for a frame.
 */
export function useGameClock(game: Game | undefined | null): number {
  const displaySeconds = game?.clock_display_seconds ?? 0;
  const running = game?.clock_running ?? false;
  const direction: ClockDirection = game?.clock_direction ?? "down";
  const limitSeconds = game?.clock_limit_seconds ?? null;

  // Identifies the reading the stopwatch below is measuring against. Elapsed
  // time means nothing once any part of the reading has changed.
  const readingKey = `${displaySeconds}|${running}|${direction}|${limitSeconds}`;

  const [stopwatch, setStopwatch] = useState(() => ({
    key: readingKey,
    elapsedMs: 0,
  }));

  useEffect(() => {
    setStopwatch({ key: readingKey, elapsedMs: 0 });
    if (!running) return;

    const startedAt = Date.now();
    const timer = window.setInterval(
      () =>
        setStopwatch({ key: readingKey, elapsedMs: Date.now() - startedAt }),
      TICK_MS,
    );
    return () => window.clearInterval(timer);
  }, [readingKey, running]);

  // A reading that has just changed starts again from zero, without waiting
  // for the effect above to reset the stopwatch.
  const elapsedSeconds =
    stopwatch.key === readingKey ? Math.floor(stopwatch.elapsedMs / 1000) : 0;

  return readClock(
    { displaySeconds, running, direction, limitSeconds },
    elapsedSeconds,
  );
}
